import { initStoreTimes } from './initStoreTimes.js'
import { pgKeyFormFields } from './storePgKeys.js'

/**
 * **매장 상세 응답 → 편집 폼, 한 곳.** 의존성 없는 순수 모듈이다(Vue·스토어·`@/` 별칭 금지).
 *
 * 같은 50여 줄이 두 벌 있었다 — `storeFormMixin.up()`(사업장 관리자·매장 관리자 화면)과 사업장 트리의
 * `StoreFormContainer`. 둘은 같은 일을 하면서 사소하게 어긋났고(`title` 의 출처, 잠금 해제 위치),
 * 잠금 규칙을 고칠 때 한쪽만 고쳐져 **같은 매장을 다시 누르면 저장이 영구히 잠기는** 결함이 됐다.
 * 그래서 「무엇을 폼으로 삼는가」는 이 파일이, 「언제 읽고 언제 잠그는가」는 믹스인 한 곳이 답한다.
 *
 * 원본 응답을 **바꾸지 않는다** — 종전 두 벌은 응답 객체를 그 자리에서 고쳐 폼으로 썼다.
 */

/**
 * **매장 폼의 탭 — 저장 본문 하나(`inputForm`)를 나눠 그리는 탭들, 그리는 순서대로.**
 *
 * 그리는 곳은 `components/store/StoreFormTabs.vue` 하나이고, 매장 폼을 가진 세 화면 — 매장 정보 관리(`StoreInfoView`) ·
 * 매장 관리(`StoreListView`, 사업장 관리자·관리점·운영사) · 사업장 트리(`StoreFormContainer`) — 이 모두 그 컴포넌트를 쓴다.
 * 화면마다 탭을 적던 동안 4월 재구성(`e081866`)이 두 화면만 고쳐, 매장 관리에서 「매장 소개·원산지」·「알림톡 설정」이
 * 빠졌다(R-S). 그 컴포넌트의 탭 이름·순서가 이 목록과 같은지는 단위 시험이 소스로 고정한다.
 *
 * 화면 고유의 탭(샵인샵·외부 채널 연동·우리동네TV 쿠폰)은 저장 본문 밖의 기능이라 여기 없다 — 화면이 이 뒤에 붙인다.
 */
export const STORE_FORM_TABS = Object.freeze(['info', 'content', 'alarm', 'time', 'delivery', 'cust'])

/**
 * **그 화면에 있는 탭 이름만 받는다** — 목록은 화면이 넘긴다.
 *
 * 「수정」 버튼이 `@click="up"` 이라 Element 가 넘기는 **MouseEvent 가 탭 이름 자리에 들어왔고**,
 * `activiTabStore` 가 `[object MouseEvent]` 가 되어 어느 탭도 활성이 아닌 **빈 껍데기 다이얼로그**가
 * 열렸다. 문자열이기만 하면 통과시키면 오타 하나가 같은 증상을 만든다.
 *
 * 목록을 **여러 화면의 합집합**으로 두는 것도 같은 구멍이다: 그 화면에 없는 이름이 통과해 다시 빈 탭이
 * 된다. 그래서 어휘의 주인은 탭을 그리는 화면이고, 모르는 값은 **그 화면의 첫 탭**으로 떨어진다.
 *
 * @param {*} value 탭 이름 후보 (MouseEvent·객체·오타가 섞여 들어온다)
 * @param {?Array<string>} tabs 그 화면의 `<el-tab-pane name>` 목록
 * @returns {?string} `tabs` 가 비면 `undefined` — 고를 탭이 없다
 */
export function storeFormTab(value, tabs) {
  const names = Array.isArray(tabs) ? tabs : []

  return names.indexOf(value) > -1 ? value : names[0]
}

/** 이미지 칸의 별칭 — 서버가 준 경로(`storeImg*`)를 폼의 편집 칸(`storeImgFile*`)으로 복사한다 */
const IMAGE_SLOTS = ['', '1', '2', '3', '4', '5']

/** 간단·상세 시간표의 길이 — `initStoreTimes` 가 만드는 행 수와 같다(요일 축) */
const TIME_ROWS = {
  storeTimesSimple: initStoreTimes(false).length,
  storeTimesDetail: initStoreTimes(true).length,
}

/**
 * 시간표 오류 칸의 키 — 폼 오류 객체에 미리 자리를 만들어 두어야 Element 가 그 칸의 오류를 그린다.
 *
 * 값이 아니라 **키 목록**을 돌려주는 이유: 나머지 오류 칸은 화면의 `initFormError()` 가 소유하고,
 * 이 모듈은 시간표 축만 안다.
 *
 * @returns {Array<string>} 예: `storeTimesSimple[0].tm`
 */
export function storeTimeErrorKeys() {
  return Object.keys(TIME_ROWS).reduce((keys, prop) => {
    const rows = []

    for (let i = 0; i < TIME_ROWS[prop]; i += 1) {
      rows.push(prop + '[' + i + '].tm', prop + '[' + i + '].btm')
    }

    return keys.concat(rows)
  }, [])
}

/**
 * 시간표 한 축(새 배열) — **행은 요일 서식이 정하고, 저장 행은 그 위에 요일(`week`)로 얹힌다.**
 *
 * `OperationTimeSettingPanel` 은 요일 행이 늘 있다고 보고 `storeTimesSimple[i].startTm` 을 바로 묶는다. 응답 행을
 * 그대로 축으로 쓰던 때, 영업시간 행이 없는 매장(콜팝 관리점 등록은 행을 만들지 않는다)에서 패널 렌더가 `undefined` 를
 * 읽다 죽었고 화면은 그 전 그림에 멈춰 시간·24시간·브레이크타임이 하나도 먹지 않았다.
 *
 * 저장 행이 없는 요일의 `isBatch` 는 `false` 다 — 서식의 일괄적용 기본값(평일 켬)은 새 매장 등록 폼의 것이다.
 *
 * @param {boolean} detailYn 상세(7행)인가 간단(2행)인가
 * @param {Array<object>} saved 이 축에 얹을 저장 행
 * @returns {Array<object>} 서식 길이의 새 행들
 */
function timeAxis(detailYn, saved) {
  const byWeek = saved.reduce((rows, row) => (row ? Object.assign(rows, { [row.week]: row }) : rows), {})

  return initStoreTimes(detailYn).map((row) => Object.assign({}, row, { isBatch: false }, byWeek[row.week]))
}

/**
 * 상세 응답 → 편집 폼(새 객체).
 *
 * - `statu` 는 언제나 `'U'` 다 — 상세를 읽었다는 것이 곧 수정이다.
 * - `title` 은 화면이 정한다(트리는 매장명, 나머지는 「매장 수정」). 안 주면 매장명으로 떨어진다.
 * - 시간표는 **쓰는 쪽만** 응답 값이고 나머지 한 축은 빈 서식이다(`detailYn` 이 고른다). 두 축을 함께
 *   채우면 사람이 스위치를 바꿀 때 안 보이던 값이 되살아난다. 어느 축이든 요일 행을 모두 갖는다([timeAxis]).
 * - 임시중단 만료 시각 `pausedUntil`(R-E1)은 **언제나 칸을 갖는다**(없으면 `null`) — 폼 위의 임시중단 카드가
 *   걸기·해제 응답으로 이 칸을 바꾼다(`.sync`). 구 서버 응답처럼 칸이 없던 객체에 나중에 생긴 속성은 Vue 2 가
 *   지켜보지 않아 카드가 갱신되지 않는다. 저장 본문에 실려도 서버는 읽기 전용이라 무시한다.
 * - PG 칸은 `utils/storePgKeys` 의 [pgKeyFormFields] 가 얹는다 — PG사별 저장값 장부(`pgKeyBook`, 저장 본문에 실려도 서버는 모르는
 *   칸이라 버린다). MID·라이센스 키는 응답 그대로다(미사용이어도 — 화면이 비워 그린다).
 *
 * @param {?object} result `GET /store/{cd}`·`GET /store/info` 응답
 * @param {?{title: ?string}} options 화면이 정하는 것
 * @returns {object} 새 폼 객체
 */
export function storeDetailForm(result, options) {
  const detail = result && typeof result === 'object' ? result : {}
  const config = options || {}
  const saved = Array.isArray(detail.storeTimes) ? detail.storeTimes : []

  const images = IMAGE_SLOTS.reduce(
    (slots, slot) => Object.assign(slots, { ['storeImgFile' + slot]: detail['storeImg' + slot] }),
    {}
  )

  return Object.assign({}, detail, images, pgKeyFormFields(detail), {
    statu: 'U',
    title: config.title || detail.storeNmLan || '',
    delFiles: [],
    storeTimesSimple: timeAxis(false, detail.detailYn ? [] : saved),
    storeTimesDetail: timeAxis(true, detail.detailYn ? saved : []),
    pausedUntil: detail.pausedUntil || null,
  })
}
